/* 三國逗陣打 · 音效與背景音樂（Web Audio 即時合成，不需要音檔）
 * 音效依與鏡頭的距離衰減、左右聲道定位；同一種音效 50ms 內不重複，避免小兵混戰時爆音。
 * 音樂：D 小調五聲音階（D F G A C）的撥弦旋律＋低音＋太鼓，96 BPM。 */
// @ts-nocheck — 第 2 步只把檔案改成模組，型別在第 4 步補
import { SG } from '../core/sg.ts';

let ctx = null, master = null, sfxBus = null, musicBus = null, noise = null;
const last = {};
const settings = { sfx: true, music: true, vol: 0.8 };
try { Object.assign(settings, JSON.parse(localStorage.getItem('sankuo-audio') || '{}')); } catch (e) { /* 用預設值 */ }
const save = () => { try { localStorage.setItem('sankuo-audio', JSON.stringify(settings)); } catch (e) { /* 忽略 */ } };

function init() {
  if (ctx) { if (ctx.state === 'suspended') ctx.resume(); return; }
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return;
  ctx = new AC();
  master = ctx.createGain(); master.gain.value = settings.vol; master.connect(ctx.destination);
  const comp = ctx.createDynamicsCompressor(); comp.threshold.value = -18; comp.ratio.value = 6; comp.connect(master);
  sfxBus = ctx.createGain(); sfxBus.gain.value = settings.sfx ? 1 : 0; sfxBus.connect(comp);
  musicBus = ctx.createGain(); musicBus.gain.value = settings.music ? 0.22 : 0; musicBus.connect(master);
  noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const d = noise.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  startMusic();
}
['pointerdown', 'keydown', 'touchend'].forEach((ev) => window.addEventListener(ev, init, { passive: true })); // iOS 有時要等手指放開才允許出聲

// ---------- 合成積木 ----------
function out(gain, pan) {
  const g = ctx.createGain(); g.gain.value = gain;
  if (ctx.createStereoPanner) { const p = ctx.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, pan || 0)); g.connect(p); p.connect(sfxBus); }
  else g.connect(sfxBus);
  return g;
}
function tone(dst, type, f0, f1, t0, dur, vol) {
  const o = ctx.createOscillator(), g = ctx.createGain();
  o.type = type; o.frequency.setValueAtTime(f0, t0); if (f1) o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(vol, t0 + 0.006); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g); g.connect(dst); o.start(t0); o.stop(t0 + dur + 0.02);
}
function hiss(dst, type, f0, f1, t0, dur, vol, q) {
  const s = ctx.createBufferSource(); s.buffer = noise; s.loop = true;
  const f = ctx.createBiquadFilter(); f.type = type; f.Q.value = q || 1; f.frequency.setValueAtTime(f0, t0); if (f1) f.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t0 + dur);
  const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t0); g.gain.exponentialRampToValueAtTime(vol, t0 + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  s.connect(f); f.connect(g); g.connect(dst); s.start(t0, Math.random() * 0.5); s.stop(t0 + dur + 0.02);
}
const PENTA = [293.66, 349.23, 392, 440, 523.25, 587.33, 698.46, 783.99, 880];

const SOUNDS = {
  swing: (d, t) => hiss(d, 'bandpass', 1800, 450, t, 0.16, 0.5, 1.2),
  heavy: (d, t) => { hiss(d, 'bandpass', 900, 200, t, 0.26, 0.6, 1); tone(d, 'sine', 110, 60, t + 0.05, 0.2, 0.25); },
  thrust: (d, t) => { hiss(d, 'highpass', 2500, 1200, t, 0.09, 0.4); tone(d, 'triangle', 700, 300, t, 0.07, 0.15); },
  twang: (d, t) => { tone(d, 'triangle', 240, 170, t, 0.28, 0.35); hiss(d, 'highpass', 4000, 0, t, 0.04, 0.2); },
  whoosh: (d, t) => hiss(d, 'lowpass', 600, 2200, t, 0.3, 0.35),
  hit: (d, t) => { tone(d, 'sine', 150, 55, t, 0.13, 0.55); hiss(d, 'lowpass', 1800, 300, t, 0.06, 0.45); },
  hitSmall: (d, t) => { tone(d, 'sine', 190, 80, t, 0.08, 0.25); hiss(d, 'lowpass', 2000, 500, t, 0.04, 0.2); },
  boom: (d, t) => { tone(d, 'sine', 85, 32, t, 0.7, 0.8); hiss(d, 'lowpass', 900, 80, t, 0.6, 0.6); },
  thunder: (d, t) => { hiss(d, 'highpass', 3000, 400, t, 0.12, 0.7); tone(d, 'sine', 70, 30, t + 0.03, 1.1, 0.7); hiss(d, 'lowpass', 500, 60, t + 0.05, 1.2, 0.55); },
  fire: (d, t) => { hiss(d, 'bandpass', 700, 1400, t, 0.7, 0.45, 0.8); hiss(d, 'lowpass', 300, 120, t, 0.5, 0.35); },
  crack: (d, t) => { tone(d, 'square', 95, 50, t, 0.12, 0.2); hiss(d, 'bandpass', 1200, 300, t, 0.15, 0.4, 2); },
  wave: (d, t) => { tone(d, 'sine', 300, 900, t, 0.35, 0.25); hiss(d, 'bandpass', 800, 2400, t, 0.4, 0.35, 1.5); },
  rain: (d, t) => { for (let i = 0; i < 9; i++) hiss(d, 'highpass', 3500, 2000, t + i * 0.08 + Math.random() * 0.04, 0.05, 0.25); },
  heal: (d, t) => [0, 2, 4, 6].forEach((n, i) => tone(d, 'sine', PENTA[n] * 2, 0, t + i * 0.07, 0.35, 0.18)),
  magic: (d, t) => { tone(d, 'triangle', 520, 1180, t, 0.3, 0.22); tone(d, 'sine', 780, 1560, t + 0.05, 0.28, 0.12); },
  roar: (d, t) => {
    const o = ctx.createOscillator(), f = ctx.createBiquadFilter(), g = ctx.createGain(), lfo = ctx.createOscillator(), lg = ctx.createGain();
    o.type = 'sawtooth'; o.frequency.setValueAtTime(150, t); o.frequency.linearRampToValueAtTime(110, t + 0.6);
    lfo.frequency.value = 18; lg.gain.value = 12; lfo.connect(lg); lg.connect(o.frequency);
    f.type = 'lowpass'; f.frequency.value = 900; g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.4, t + 0.05); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.65);
    o.connect(f); f.connect(g); g.connect(d); o.start(t); lfo.start(t); o.stop(t + 0.7); lfo.stop(t + 0.7);
  },
  shield: (d, t) => { tone(d, 'sine', 420, 640, t, 0.35, 0.2); tone(d, 'sine', 630, 960, t, 0.35, 0.1); },
  puff: (d, t) => hiss(d, 'lowpass', 1200, 300, t, 0.25, 0.3),
  hop: (d, t) => tone(d, 'sine', 260, 520, t, 0.1, 0.18),
  down: (d, t) => { tone(d, 'sine', 100, 40, t, 0.25, 0.45); hiss(d, 'lowpass', 600, 100, t, 0.2, 0.3); },
  coin: (d, t) => { tone(d, 'square', 1318, 0, t, 0.07, 0.08); tone(d, 'square', 1760, 0, t + 0.06, 0.12, 0.08); },
  levelup: (d, t) => [0, 2, 3, 5, 8].forEach((n, i) => tone(d, 'triangle', PENTA[n], 0, t + i * 0.08, 0.4, 0.2)),
  gong: (d, t) => { [[110, 0.45], [174, 0.25], [282, 0.15], [415, 0.08]].forEach(([f, v]) => tone(d, 'sine', f, f * 0.98, t, 2.4, v)); hiss(d, 'bandpass', 2000, 800, t, 0.3, 0.2); },
  drum: (d, t) => { tone(d, 'sine', 95, 48, t, 0.4, 0.8); hiss(d, 'lowpass', 400, 80, t, 0.12, 0.5); tone(d, 'sine', 95, 48, t + 0.18, 0.35, 0.6); },
  ult: (d, t) => { hiss(d, 'lowpass', 400, 3000, t, 0.35, 0.5); tone(d, 'sawtooth', 110, 220, t, 0.4, 0.12); SOUNDS.gong(d, t + 0.15); },
  arrow: (d, t) => { tone(d, 'triangle', 900, 500, t, 0.06, 0.1); hiss(d, 'highpass', 3000, 0, t, 0.08, 0.12); },
  click: (d, t) => tone(d, 'square', 900, 0, t, 0.03, 0.05),
  // 元素反應
  shatter: (d, t) => { for (let i = 0; i < 6; i++) tone(d, 'triangle', 2400 + Math.random() * 1800, 900, t + i * 0.025, 0.12, 0.12); hiss(d, 'highpass', 5000, 2000, t, 0.2, 0.3); },
  zap: (d, t) => { for (let i = 0; i < 4; i++) tone(d, 'sawtooth', 180 + Math.random() * 120, 60, t + i * 0.05, 0.08, 0.14); hiss(d, 'highpass', 4000, 1500, t, 0.25, 0.35); },
  sizzle: (d, t) => { hiss(d, 'highpass', 3000, 6000, t, 0.6, 0.35); hiss(d, 'bandpass', 1200, 400, t, 0.4, 0.2, 2); },
  chime: (d, t) => [8, 10, 12].forEach((n, i) => tone(d, 'sine', (PENTA[n % PENTA.length] || 880) * 2, 0, t + i * 0.05, 0.6, 0.14)),
  gust: (d, t) => hiss(d, 'bandpass', 400, 1600, t, 0.55, 0.45, 1.5),
  drainS: (d, t) => { tone(d, 'sine', 400, 120, t, 0.5, 0.22); tone(d, 'triangle', 200, 80, t, 0.5, 0.12); },
  death: (d, t) => { tone(d, 'sine', 220, 70, t, 0.6, 0.35); hiss(d, 'lowpass', 800, 100, t, 0.4, 0.25); },
};

// 播放：x、y 為世界座標（省略則不做距離衰減）
function play(name, x, y, view, vol) {
  if (!ctx || !settings.sfx || !SOUNDS[name]) return;
  const now = ctx.currentTime;
  if (last[name] && now - last[name] < 0.05) return;
  last[name] = now;
  let g = vol == null ? 1 : vol, pan = 0;
  if (x != null && view) {
    const d = Math.hypot(x - view.cam.x, (y - view.cam.y) * 1.2);
    g *= Math.max(0, 1 - d / 1500);
    if (g < 0.03) return;
    pan = (x - view.cam.x) / 900;
  }
  SOUNDS[name](out(g, pan), now + 0.005);
}

const ARCH_SND = { crescent: 'heavy', dash: 'thrust', fire: 'magic', command: 'gong', roar: 'roar', slam: 'heavy', bolt: 'whoosh', volley: 'twang', spin: 'swing', buff: 'magic',
  heal: 'heal', blink: 'puff', hook: 'whoosh', thunder: 'magic', shield: 'shield', charge: 'heavy', trap: 'crack', spikes: 'heavy', multi: 'swing', summon: 'drum', stealth: 'puff' };
const REACT_SND = { melt: 'sizzle', shatter: 'shatter', conduct: 'zap', firestorm: 'fire', burst: 'boom', spread: 'gust', purge: 'chime', drain: 'drainS', crystal: 'crack', freeze: 'shatter', steam: 'sizzle', wildfire: 'fire' };
const VFX_SND = { recall: 'magic', recalldone: 'shield', fire: 'fire', thunder: 'thunder', slam: 'boom', spike: 'crack', crescent: 'wave', rain: 'rain', heal: 'heal', puff: 'puff', spark: 'hit' };

SG.Audio = {
  init, play, settings,
  set(k, v) {
    settings[k] = v; save();
    if (!ctx) return;
    if (k === 'sfx') sfxBus.gain.value = v ? 1 : 0;
    if (k === 'music') musicBus.gain.value = v ? 0.22 : 0;
    if (k === 'vol') master.gain.value = v;
  },
  // 角色動作開始時的音效
  anim(u, anim, view) {
    const minor = u.kind === 'minion' || u.kind === 'creep';
    const v = minor ? 0.3 : u.isPlayer ? 1 : 0.7;
    if (anim === 'attack') { const w = u.spec && u.spec.weapon; play(w === 'bow' ? 'twang' : w === 'fan' || w === 'staff' ? 'whoosh' : w === 'spear' || w === 'snake' ? 'thrust' : 'swing', u.x, u.y, view, v); }
    else if (anim === 'hit') play(minor ? 'hitSmall' : 'hit', u.x, u.y, view, v);
    else if (anim === 'jump') play('hop', u.x, u.y, view, v * 0.8);
    else if (anim === 'fall') play(u.alive === false && !minor ? 'death' : 'down', u.x, u.y, view, v);
    else if (/^s\d$/.test(anim) && u.spec && u.spec.skills) { const sk = u.spec.skills[+anim[1]]; if (sk) play(ARCH_SND[sk.arch] || 'magic', u.x, u.y, view, v); }
  },
  vfx(o, view) {
    if (o.k === 'react') { const snd = REACT_SND[o.r]; if (snd) play(snd, o.x, o.y, view, 1); return; }
    if (VFX_SND[o.k]) play(VFX_SND[o.k], o.x, o.y, view, 0.8);
  },
  proj(p, view) { if (p.tower) play('arrow', p.x, p.y, view, 0.6); },
};

// ---------- 背景音樂 ----------
// 手寫旋律（不再隨機生成），D 小調五聲音階（D F G A C）。
// 樂器：古箏／琵琶用 Karplus-Strong 撥弦合成（預先算好每個音的波形），笛子用柔和正弦＋晚起的顫音，
// 低音與墊底和聲用過濾後的三角波，鼓只有輕的低音鼓與小鈴。全部送進一個淡淡的殘響。
// 平靜、戰鬥各三段（v0.3.4），每段隨機調性、速度、主奏與伴奏音型；平靜時偶爾插 4 小節間奏。intensity 超過 0.45 時下一段換成戰鬥曲。
// 列傳地圖可帶自己的配樂（SG.Audio.setTheme）：曲目、音階、調、速度、主奏、鼓的風格；null 換回原本的曲子。換配樂在下一小節生效
let intensity = 0, intensityT = 0, theme = null, themeDirty = false;
function startMusic() {
  const sr = ctx.sampleRate;
  // 殘響
  const verb = ctx.createConvolver(), len = sr * 2.2, ir = ctx.createBuffer(2, len, sr);
  for (let ch = 0; ch < 2; ch++) { const d = ir.getChannelData(ch); for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 4); }
  verb.buffer = ir;
  const wet = ctx.createGain(); wet.gain.value = 0.22; verb.connect(wet); wet.connect(musicBus);
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 6000; lp.connect(musicBus);
  const send = (node, amt) => { node.connect(lp); if (amt) { const g = ctx.createGain(); g.gain.value = amt; node.connect(g); g.connect(verb); } };

  // Karplus-Strong：每個頻率算一次，之後重播
  const ksCache = {};
  function ks(f, bright) {
    const key = Math.round(f * 10) + (bright ? 'b' : '');
    if (ksCache[key]) return ksCache[key];
    const dur = bright ? 1.4 : 2.4, n = Math.floor(sr * dur), buf = ctx.createBuffer(1, n, sr), y = buf.getChannelData(0);
    const N = Math.max(2, Math.round(sr / f)), decay = bright ? 0.992 : 0.996;
    let prev = 0;
    for (let i = 0; i < N; i++) { const r = Math.random() * 2 - 1; y[i] = bright ? r : (r + prev) * 0.5; prev = r; } // 初始雜訊（古箏先做一次低通，聲音較溫潤）
    for (let i = N; i < n; i++) y[i] = decay * 0.5 * (y[i - N] + y[i - N - 1 < 0 ? 0 : i - N - 1]);
    let peak = 0; for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(y[i]));
    if (peak > 0) for (let i = 0; i < n; i++) y[i] /= peak;
    return (ksCache[key] = buf);
  }
  function pluck(f, t, v, bright) {
    const src = ctx.createBufferSource(), g = ctx.createGain(), pan = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
    src.buffer = ks(f, bright); g.gain.value = v;
    src.connect(g);
    if (pan) { pan.pan.value = bright ? 0.25 : -0.2; g.connect(pan); send(pan, 0.45); } else send(g, 0.45);
    src.start(t);
  }
  function flute(f, t, dur, v) {
    const o = ctx.createOscillator(), o2 = ctx.createOscillator(), lfo = ctx.createOscillator(), lg = ctx.createGain(), g = ctx.createGain(), g2 = ctx.createGain(), fl = ctx.createBiquadFilter();
    o.type = 'sine'; o2.type = 'sine'; o.frequency.value = f; o2.frequency.value = f * 2; g2.gain.value = 0.06;
    lfo.frequency.value = 5; lg.gain.setValueAtTime(0, t); lg.gain.setValueAtTime(0, t + Math.min(0.35, dur * 0.4)); lg.gain.linearRampToValueAtTime(f * 0.007, t + Math.min(0.9, dur * 0.8));
    lfo.connect(lg); lg.connect(o.frequency);
    fl.type = 'lowpass'; fl.frequency.value = 2800;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(v, t + 0.07); g.gain.setValueAtTime(v, t + Math.max(0.08, dur - 0.12)); g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.25);
    o.connect(g); o2.connect(g2); g2.connect(g); g.connect(fl); send(fl, 0.6);
    o.start(t); o2.start(t); lfo.start(t); const end = t + dur + 0.3; o.stop(end); o2.stop(end); lfo.stop(end);
  }
  function pad(fs, t, dur, v) {
    const g = ctx.createGain(), fl = ctx.createBiquadFilter(); fl.type = 'lowpass'; fl.frequency.value = 800;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(v, t + 0.6); g.gain.setValueAtTime(v, t + dur - 0.3); g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.8);
    g.connect(fl); send(fl, 0.5);
    fs.forEach((f) => [0.997, 1.003].forEach((d) => { const o = ctx.createOscillator(); o.type = 'triangle'; o.frequency.value = f * d; o.connect(g); o.start(t); o.stop(t + dur + 0.9); }));
  }
  function bass(f, t, dur, v) {
    const o = ctx.createOscillator(), g = ctx.createGain(), fl = ctx.createBiquadFilter(); o.type = 'triangle'; o.frequency.value = f; fl.type = 'lowpass'; fl.frequency.value = 400;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(v, t + 0.03); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(fl); fl.connect(g); send(g, 0.08); o.start(t); o.stop(t + dur + 0.05);
  }
  function drum(t, v, low) {
    const o = ctx.createOscillator(), g = ctx.createGain(); o.type = 'sine';
    o.frequency.setValueAtTime(low ? 90 : 160, t); o.frequency.exponentialRampToValueAtTime(low ? 45 : 90, t + 0.25);
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(v, t + 0.005); g.gain.exponentialRampToValueAtTime(0.0001, t + (low ? 0.5 : 0.2));
    o.connect(g); send(g, 0.15); o.start(t); o.stop(t + 0.55);
  }
  function bell(f, t, v) { // 小鈴：兩個不和諧比例的正弦，短促
    [1, 2.76].forEach((m, i) => { const o = ctx.createOscillator(), g = ctx.createGain(); o.type = 'sine'; o.frequency.value = f * m; g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(v * (i ? 0.3 : 1), t + 0.004); g.gain.exponentialRampToValueAtTime(0.0001, t + 1.2); o.connect(g); send(g, 0.7); o.start(t); o.stop(t + 1.3); });
  }

  function xun(f, t, dur, v) { // 塤：低沉的正弦加一點氣聲，顫音慢而淺
    const o = ctx.createOscillator(), lfo = ctx.createOscillator(), lg = ctx.createGain(), g = ctx.createGain(), fl = ctx.createBiquadFilter();
    o.type = 'sine'; o.frequency.value = f;
    lfo.frequency.value = 3.6; lg.gain.setValueAtTime(0, t); lg.gain.linearRampToValueAtTime(f * 0.005, t + Math.min(1.2, dur));
    lfo.connect(lg); lg.connect(o.frequency);
    fl.type = 'lowpass'; fl.frequency.value = 1400;
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(v, t + 0.12); g.gain.setValueAtTime(v, t + Math.max(0.13, dur - 0.15)); g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.35);
    o.connect(g); g.connect(fl); send(fl, 0.7);
    const end = t + dur + 0.4; o.start(t); lfo.start(t); o.stop(end); lfo.stop(end);
    const s = ctx.createBufferSource(), bf = ctx.createBiquadFilter(), bg = ctx.createGain(); // 氣聲
    s.buffer = noise; s.loop = true; bf.type = 'bandpass'; bf.frequency.value = f * 3; bf.Q.value = 2;
    bg.gain.setValueAtTime(0.0001, t); bg.gain.exponentialRampToValueAtTime(v * 0.35, t + 0.05); bg.gain.exponentialRampToValueAtTime(0.0001, t + Math.min(0.5, dur));
    s.connect(bf); bf.connect(bg); send(bg, 0.5); s.start(t, Math.random() * 0.5); s.stop(t + Math.min(0.55, dur + 0.05));
  }
  function gongM(t, v) { // 低鑼：幾個不和諧泛音，長長地散開
    [[98, 1], [156, 0.55], [251, 0.3], [372, 0.15]].forEach(([f, m]) => { const o = ctx.createOscillator(), g = ctx.createGain(); o.type = 'sine'; o.frequency.setValueAtTime(f, t); o.frequency.exponentialRampToValueAtTime(f * 0.985, t + 3); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(v * m, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + 3.2); o.connect(g); send(g, 0.6); o.start(t); o.stop(t + 3.3); });
  }

  // 音級 → 頻率：0 = D3（146.83），預設五聲 D F G A C（配樂可換音階），可負可超過一個八度；transpose 以半音為單位
  let STEPS = [0, 3, 5, 7, 10];
  let transpose = 0;
  const hz = (deg, base) => { const o = Math.floor(deg / 5), d = ((deg % 5) + 5) % 5; return (base || 146.83) * Math.pow(2, (STEPS[d] + 12 * o + transpose) / 12); };

  // [音級, 拍數]；null 為休止。旋律音級以 D4 為 5。v0.3.4：平靜、戰鬥各三段，隨機輪替（音樂太固定很惱人）
  const PIECES = {
    calm: [
      { name: '赤壁', mel: [
        [[8, 1.5], [7, 0.5], [8, 1], [9, 1]], [[10, 2], [9, 1], [8, 1]], [[7, 1.5], [8, 0.5], [7, 1], [6, 1]], [[5, 3], [null, 1]],
        [[6, 1], [7, 1], [8, 1], [9, 1]], [[10, 1.5], [11, 0.5], [10, 1], [9, 1]], [[8, 1], [7, 1], [6, 1], [7, 1]], [[8, 3], [null, 1]],
        [[10, 1.5], [9, 0.5], [8, 1], [7, 1]], [[8, 2], [9, 1], [10, 1]], [[11, 1.5], [10, 0.5], [9, 1], [8, 1]], [[7, 3], [null, 1]],
        [[8, 1], [9, 1], [10, 1], [9, 1]], [[8, 1.5], [7, 0.5], [6, 1], [7, 1]], [[8, 1], [7, 0.5], [6, 0.5], [5, 1], [6, 1]], [[5, 4]],
      ], roots: [0, 1, 2, 0, 1, 0, 2, 3, 0, 3, 1, 2, 1, 4, 2, 0] },
      { name: '江上', mel: [
        [[5, 1], [6, 1], [8, 2]], [[7, 1], [8, 0.5], [7, 0.5], [6, 2]], [[5, 1], [3, 1], [5, 2]], [[6, 3], [null, 1]],
        [[8, 1], [9, 1], [10, 1.5], [9, 0.5]], [[8, 1], [7, 1], [6, 2]], [[7, 1], [6, 0.5], [5, 0.5], [3, 1], [5, 1]], [[5, 4]],
      ], roots: [0, 1, 0, 2, 1, 3, 4, 0] },
      { name: '月下', mel: [
        [[10, 2], [9, 1], [10, 1]], [[8, 1.5], [7, 0.5], [6, 2]], [[7, 1], [8, 1], [9, 1], [8, 1]], [[7, 3], [null, 1]],
        [[5, 1], [7, 1], [8, 1], [10, 1]], [[9, 2], [8, 1], [7, 1]], [[6, 1], [5, 1], [6, 1], [7, 1]], [[5, 4]],
      ], roots: [3, 1, 2, 4, 0, 2, 1, 0] },
    ],
    battle: [
      { name: '鏖兵', mel: [
        [[10, 0.5], [10, 0.5], [9, 0.5], [8, 0.5], [9, 1], [10, 1]], [[11, 0.5], [10, 0.5], [9, 0.5], [8, 0.5], [7, 2]],
        [[8, 0.5], [9, 0.5], [10, 0.5], [9, 0.5], [8, 1], [7, 1]], [[6, 1], [7, 1], [8, 2]],
        [[10, 0.5], [10, 0.5], [11, 0.5], [12, 0.5], [11, 1], [10, 1]], [[9, 0.5], [10, 0.5], [9, 0.5], [8, 0.5], [7, 2]],
        [[8, 0.5], [7, 0.5], [6, 0.5], [7, 0.5], [8, 1], [9, 1]], [[10, 4]],
      ], roots: [0, 2, 1, 3, 0, 4, 2, 0] },
      { name: '破陣', mel: [
        [[5, 0.5], [7, 0.5], [8, 0.5], [10, 0.5], [9, 1], [8, 1]], [[7, 0.5], [8, 0.5], [7, 0.5], [6, 0.5], [5, 2]],
        [[8, 0.5], [8, 0.5], [10, 0.5], [8, 0.5], [11, 1], [10, 1]], [[9, 1], [8, 1], [7, 2]],
        [[12, 1], [11, 0.5], [10, 0.5], [9, 1], [10, 1]], [[8, 0.5], [9, 0.5], [10, 0.5], [11, 0.5], [12, 2]],
        [[11, 0.5], [10, 0.5], [9, 0.5], [8, 0.5], [7, 1], [8, 1]], [[5, 4]],
      ], roots: [0, 4, 2, 3, 0, 1, 4, 0] },
      { name: '疾風', mel: [
        [[8, 0.5], [null, 0.5], [8, 0.5], [9, 0.5], [10, 1], [8, 1]], [[7, 0.5], [null, 0.5], [7, 0.5], [8, 0.5], [9, 2]],
        [[10, 0.5], [9, 0.5], [8, 0.5], [7, 0.5], [8, 0.5], [9, 0.5], [10, 1]], [[11, 2], [10, 2]],
        [[8, 0.5], [null, 0.5], [8, 0.5], [9, 0.5], [10, 1], [12, 1]], [[11, 0.5], [10, 0.5], [9, 1], [8, 2]],
        [[7, 0.5], [8, 0.5], [9, 0.5], [10, 0.5], [9, 1], [7, 1]], [[8, 4]],
      ], roots: [0, 2, 1, 3, 0, 2, 4, 0] },
    ],
  };
  const ARPS = [[5, 8, 10, 8], [5, 7, 8, 10], [10, 8, 5, 8], [5, 10, 8, 7]];
  const KEYS = [0, 5, 7, -2]; // D、G、A、C
  const LEADS = ['flute', 'pluck', 'both'];
  const DEFAULT = { pieces: PIECES, steps: STEPS, keys: KEYS, bpm: { calm: [72, 86], battle: [100, 116] }, leads: { calm: LEADS, battle: LEADS }, drums: 'light' };
  const pickOther = (n, last) => { if (n < 2) return 0; let k; do { k = Math.floor(Math.random() * n); } while (k === last); return k; };
  // 一段樂曲的設定：每次換段都重抽（曲目、調、速度、主奏、伴奏音型）
  let piece = 'calm', cur = null, lastIdx = { calm: -1, battle: -1 }, interlude = 0;
  const any = (a) => a[Math.floor(Math.random() * a.length)];
  function choose(kind) {
    piece = kind;
    const T = theme || DEFAULT, list = T.pieces[kind], [b0, b1] = T.bpm[kind];
    const idx = pickOther(list.length, lastIdx[kind]); lastIdx[kind] = idx;
    cur = Object.assign({}, list[idx], { bpm: b0 + Math.random() * (b1 - b0), lead: any(T.leads[kind]), arp: any(ARPS), drums: T.drums });
    STEPS = T.steps; transpose = any(T.keys);
  }
  choose('calm');

  // 樂句排程：一次排一小節
  let next = ctx.currentTime + 0.4, bar = 0;
  function scheduleBar() {
    const battle = piece === 'battle', beat = 60 / cur.bpm, t0 = next;
    // 間奏（只在平靜時）：和聲墊底＋零星撥弦，讓耳朵休息
    if (interlude > 0) {
      const root = [0, 2, 1, 0][interlude % 4], rf = hz(root, 73.42);
      bass(rf, t0, beat * 3.8, 0.18); pad([hz(root, 146.83), hz(root + 3, 146.83), hz(root + 5, 146.83)], t0, beat * 4, 0.03);
      if (Math.random() < 0.6) pluck(hz(root + 5 + Math.floor(Math.random() * 5)), t0 + beat * (1 + Math.floor(Math.random() * 3)), 0.1, false);
      next = t0 + 4 * beat; interlude--;
      if (interlude === 0) choose(intensity > 0.45 ? 'battle' : 'calm');
      return;
    }
    const mel = cur.mel, b = bar % mel.length;
    const root = cur.roots[b % cur.roots.length], rf = hz(root, 73.42), fifth = hz(root + 3, 73.42);
    // 低音與墊底和聲
    if (battle) { [0, 1.5, 2, 3].forEach((x, i) => bass(i % 2 ? fifth : rf, t0 + x * beat, beat * 0.9, 0.3)); }
    else { bass(rf, t0, beat * 3.5, 0.28); pad([hz(root, 146.83), hz(root + 3, 146.83), hz(root + 5, 146.83)], t0, beat * 4, 0.035); }
    // 伴奏：古箏分解和弦（平靜曲八分音符、音型每段不同；戰鬥曲十六分音符的根音五度跳動）
    const arp = cur.arp.map((d) => root + d);
    if (battle) for (let i = 0; i < 8; i++) pluck(hz(i % 2 ? root + 8 : root + 5), t0 + i * beat / 2, i % 4 === 0 ? 0.22 : 0.14, false);
    else for (let i = 0; i < 8; i++) pluck(hz(arp[i % 4] - 5), t0 + i * beat / 2, i === 0 ? 0.2 : 0.12, false);
    // 旋律：主奏每段隨機（笛、撥弦或兩者）；偶爾加一個上方倚音
    let x = 0;
    for (const [deg, len] of mel[b]) {
      const t = t0 + x * beat;
      if (deg != null) {
        const f = hz(deg);
        if (len >= 1 && Math.random() < 0.12) pluck(hz(deg + 1), t - beat * 0.12, 0.1, true); // 倚音
        if (battle) {
          if (cur.lead !== 'flute') { if (len >= 1) for (let r = 0; r < len * 6; r++) pluck(f, t + r * beat / 6, r ? 0.13 : 0.26, true); else pluck(f, t, 0.26, true); }
          if (cur.lead !== 'pluck') flute(f * 2, t, len * beat * 0.95, cur.lead === 'flute' ? 0.09 : 0.05);
        } else if (cur.lead === 'xun') xun(f, t, len * beat * 0.95, 0.13);
        else {
          if (cur.lead !== 'pluck') flute(f, t, len * beat * 0.95, cur.lead === 'flute' ? 0.11 : 0.05);
          if (cur.lead !== 'flute') pluck(f, t, 0.3, true);
        }
      }
      x += len;
    }
    // 打擊：平靜曲只在每兩小節第一拍輕鼓、樂句頭一聲小鈴；戰鬥曲每拍鼓、反拍小鼓
    if (cur.drums === 'war') { // 戰鼓：戰鬥時每拍大鼓、樂句頭一聲鑼；平靜時只有遠處像心跳的兩下低鼓
      if (battle) { [0, 1, 2, 2.5, 3].forEach((i) => drum(t0 + i * beat, i === 0 || i === 2 ? 0.6 : 0.4, true)); drum(t0 + 3.5 * beat, 0.2, false); if (b === 0) gongM(t0, 0.18); }
      else { drum(t0, 0.2, true); drum(t0 + beat * 0.4, 0.13, true); }
    } else if (battle) { [0, 1, 2, 3].forEach((i) => drum(t0 + i * beat, i % 2 ? 0.32 : 0.5, true)); [0.5, 2.5, 3.5].forEach((i) => drum(t0 + i * beat, 0.16, false)); if (b === mel.length - 1) [3, 3.25, 3.5, 3.75].forEach((i) => drum(t0 + i * beat, 0.3, false)); }
    else { if (b % 2 === 0) drum(t0, 0.28, true); if (b % 4 === 0) bell(hz(10, 146.83) * 2, t0, 0.03); }
    next = t0 + 4 * beat;
    bar++;
    // 樂句結束：換一段（戰況高就戰鬥曲）；平靜時三成機率先來 4 小節間奏
    if (bar % mel.length === 0) {
      bar = 0;
      const want = intensity > 0.45 ? 'battle' : 'calm';
      if (want === 'calm' && Math.random() < 0.3) interlude = 4; else choose(want);
    }
  }
  (function tick() {
    if (!ctx) return;
    intensityT = Math.max(0, intensityT - 0.0036);
    intensity += (intensityT - intensity) * 0.05;
    if (themeDirty) { themeDirty = false; interlude = 0; bar = 0; lastIdx = { calm: -1, battle: -1 }; choose(intensity > 0.45 ? 'battle' : 'calm'); }
    // 戰鬥中途戰況升高：在小節線上直接切到戰鬥曲，不必等平靜曲 16 小節走完
    if ((piece === 'calm' || interlude > 0) && intensity > 0.6 && bar % 4 === 0) { interlude = 0; choose('battle'); bar = 0; }
    while (next < ctx.currentTime + 0.6) scheduleBar();
    setTimeout(tick, 80);
  })();
}
// 遊戲每幀回報戰況（0–1）：附近敵將數量、正在交戰
SG.Audio.setTheme = (t) => { if (t === theme) return; theme = t; themeDirty = true; };
SG.Audio.setIntensity = (v) => { intensityT = Math.max(intensityT, Math.min(1, v)); };
